import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { isLessonUnlockedForNavigation, isModuleAccessible as checkModuleAccessible } from '@/lib/courseAccess';

const CourseSidebar = ({
  course,
  userProgress,
  isVisible,
  onClose,
  courseSlug,
  userTenantRole,
  isEnrolledInCourse = false,
  isModuleAccessible = () => false,
}) => {
  const [expandedModules, setExpandedModules] = useState(['module-1']);
  const navigate = useNavigate();

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => 
      prev?.includes(moduleId)
        ? prev?.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleModuleClick = (module) => {
    if (!checkModuleAccessible({
      userTenantRole,
      isEnrolledInCourse,
      isEnrolledInModule: isModuleAccessible(module?.id),
    })) {
      return;
    }
    if (courseSlug && module.slug) {
      navigate(`/course/${courseSlug}/module/${module.slug}`);
    }
  };

  const handleLessonClick = (lessonId, moduleSlug) => {
    if (courseSlug && moduleSlug) {
      navigate(`/course/${courseSlug}/module/${moduleSlug}/lesson/${lessonId}`);
    } else {
      navigate('/video-player-interface');
    }
  };

  const getModuleProgress = (module) => {
    if (userProgress?.moduleProgress && module?.id != null) {
      const fromMap = userProgress.moduleProgress[module.id];
      if (typeof fromMap === 'number' && !isNaN(fromMap)) {
        return fromMap;
      }
    }

    if (!module?.lessons || !Array.isArray(module.lessons) || module.lessons.length === 0) {
      return 0;
    }
    
    if (!userProgress?.completedLessons || !Array.isArray(userProgress.completedLessons)) {
      return 0;
    }
    
    const completedLessons = module.lessons.filter(lesson => 
      userProgress.completedLessons.includes(String(lesson?.id))
    ).length;
    
    const totalLessons = module.lessons.length;
    
    if (totalLessons === 0) return 0;
    
    const percentage = Math.round((completedLessons / totalLessons) * 100);
    
    return isNaN(percentage) ? 0 : percentage;
  };

  const isLessonCompleted = (lessonId) => {
    return userProgress?.completedLessons?.includes(String(lessonId));
  };

  const isLessonLocked = (lesson, module, moduleIndex, lessonIndex) => {
    const moduleAccessible = checkModuleAccessible({
      userTenantRole,
      isEnrolledInCourse,
      isEnrolledInModule: isModuleAccessible(module?.id),
    });

    if (!moduleAccessible) {
      return true;
    }

    if (
      isLessonUnlockedForNavigation({
        lessonIsFree: !!lesson?.is_free,
        userTenantRole,
        isEnrolledInCourse,
        isEnrolledInModule: isModuleAccessible(module?.id),
      })
    ) {
      return false;
    }

    if (lessonIndex > 0) {
      const previousLesson = module.lessons?.[lessonIndex - 1];
      return !isLessonCompleted(previousLesson?.id);
    }

    return lessonIndex > 0;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isVisible && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-200" onClick={onClose}>
          <div className="fixed right-0 top-0 bottom-0 w-80 bg-card border-l border-border overflow-y-auto" onClick={e => e?.stopPropagation()}>
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-heading font-semibold text-card-foreground">
                  Conteúdo do Curso
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  iconName="X"
                  iconSize={20}
                />
              </div>
            </div>
            <SidebarContent 
              course={course}
              userProgress={userProgress}
              expandedModules={expandedModules}
              toggleModule={toggleModule}
              handleModuleClick={handleModuleClick}
              handleLessonClick={handleLessonClick}
              getModuleProgress={getModuleProgress}
              isLessonCompleted={isLessonCompleted}
              isLessonLocked={isLessonLocked}
              courseSlug={courseSlug}
              userTenantRole={userTenantRole}
              isEnrolledInCourse={isEnrolledInCourse}
              isModuleAccessible={isModuleAccessible}
            />
          </div>
        </div>
      )}
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-card-foreground">
            Conteúdo do Curso
          </h2>
          <p className="text-sm text-muted-foreground font-caption mt-1">
            {course?.modules?.length} módulos • {course?.lessonsCount} lições
          </p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <SidebarContent 
            course={course}
            userProgress={userProgress}
            expandedModules={expandedModules}
            toggleModule={toggleModule}
            handleModuleClick={handleModuleClick}
            handleLessonClick={handleLessonClick}
            getModuleProgress={getModuleProgress}
            isLessonCompleted={isLessonCompleted}
            isLessonLocked={isLessonLocked}
            courseSlug={courseSlug}
            userTenantRole={userTenantRole}
            isEnrolledInCourse={isEnrolledInCourse}
            isModuleAccessible={isModuleAccessible}
          />
        </div>
      </div>
    </>
  );
};

const SidebarContent = ({ 
  course, 
  userProgress, 
  expandedModules, 
  toggleModule, 
  handleModuleClick,
  handleLessonClick, 
  getModuleProgress, 
  isLessonCompleted, 
  isLessonLocked,
  courseSlug,
  userTenantRole,
  isEnrolledInCourse,
  isModuleAccessible,
}) => {
  // Helper function to get translated "Coming Soon" text
  const getComingSoonText = (language = 'pt') => {
    const translations = {
      pt: 'Em Breve',
      en: 'Coming Soon',
      es: 'Próximamente'
    };
    return translations[language] || translations.pt;
  };

  // Get current language from localStorage or default to 'pt'
  const getLanguage = () => {
    try {
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage && ['pt', 'en', 'es'].includes(savedLanguage)) {
        return savedLanguage;
      }
    } catch (e) {
      // If localStorage is not available, fallback to 'pt'
    }
    return 'pt';
  };

  return (
    <div className="p-4 space-y-4">
      {course?.modules?.map((module, moduleIndex) => {
        const isExpanded = expandedModules?.includes(module.id);
        const progress = getModuleProgress(module);
        const moduleLocked = !checkModuleAccessible({
          userTenantRole,
          isEnrolledInCourse,
          isEnrolledInModule: isModuleAccessible(module?.id),
        });
        
        return (
          <div key={module.id} className="space-y-2">
            {/* Module Header */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!moduleLocked) {
                  handleModuleClick(module);
                }
                toggleModule(module.id);
              }}
              className={`w-full flex items-center justify-between p-3 bg-muted rounded-lg transition-micro ${
                moduleLocked
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon 
                  name={moduleLocked ? 'Lock' : isExpanded ? 'ChevronDown' : 'ChevronRight'} 
                  size={30} 
                />
                <div className="text-left">
                  <p className="font-medium text-sm">{module.title}</p>
                  <p className="text-xs text-muted-foreground font-caption">
                    {module.lessons?.length} lições
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium">{progress}%</p>
                <div className="w-12 h-1 bg-border rounded-full mt-1">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </button>
            {/* Module Lessons */}
            {isExpanded && (
              <div className="ml-4 space-y-1">
                {module.lessons?.map((lesson, lessonIndex) => {
                  const completed = isLessonCompleted(lesson?.id);
                  const locked = isLessonLocked(lesson, module, moduleIndex, lessonIndex);
                  
                  return (
                    <button
                      key={lesson?.id}
                      onClick={() => !locked && handleLessonClick(lesson?.id, module.slug)}
                      disabled={locked}
                      className={`w-full flex items-center space-x-3 p-2 rounded-lg text-left transition-micro ${
                        locked 
                          ? 'opacity-50 cursor-not-allowed' :'hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        completed 
                          ? 'bg-success text-success-foreground'
                          : locked
                          ? 'bg-muted text-muted-foreground'
                          : 'bg-primary text-primary-foreground'
                      }`}>
                        <Icon 
                          name={completed ? "Check" : locked ? "Lock" : "Play"} 
                          size={12} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {locked ? `Aula ${lesson?.order || lessonIndex + 1}...` : lesson?.title}
                        </p>
                        <p className="text-xs text-muted-foreground font-caption">
                          {(() => {
                            // Check if duration is valid
                            const isValidDuration = lesson?.duration != null && 
                                                    typeof lesson?.duration === 'number' && 
                                                    !isNaN(lesson?.duration);
                            
                            // Show "Coming Soon" if locked OR if duration is invalid
                            if (locked || !isValidDuration) {
                              return getComingSoonText(getLanguage());
                            }
                            
                            // Format duration as MM:SS
                            const minutes = Math.floor(lesson.duration / 60);
                            const seconds = lesson.duration % 60;
                            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                          })()}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CourseSidebar;